import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { cva, VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import { AnchorHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils/classnameVariants';
import { Colors } from '../../lib/utils/colors';
import { focusRingClasses } from '../../lib/utils/constants/accessbilityClasses';

const ActionListItemTestId = 'action-list-item-test';

export interface ActionListItemContent {
  text: string;
  sidetext?: string | React.ReactNode;
  link: string;
  onActionClick?: () => void;
}

export const ActionListItemVariants = ['default', ...Colors] as const;

type CvaConfig = {
  variants: {
    variant: { [key in (typeof ActionListItemVariants)[number]]: string };
  };
  defaultVariants: { variant: (typeof ActionListItemVariants)[number] };
};

const actionListVariants = cva(
  'group flex h-[72px] w-full items-center justify-between overflow-auto rounded-xs rounded-bl-none px-6 text-black-900 focus-visible:rounded-xs',
  {
    variants: {
      variant: {
        default: 'border border-black-50 bg-white hover:border-black-900 hover:bg-black-900 hover:text-white',
        lightblue: 'bg-lightblue-50 hover:bg-lightblue-100',
        orange: 'bg-orange-50 hover:bg-orange-100',
        green: 'bg-green-50 hover:bg-green-100',
        purple: 'bg-purple-50 hover:bg-purple-100',
        yellow: 'bg-yellow-50 hover:bg-yellow-100',
        blue: 'bg-blue-50 hover:bg-blue-100',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  } satisfies CvaConfig,
);

export interface ActionListItemProps
  extends ActionListItemContent,
    VariantProps<typeof actionListVariants>,
    AnchorHTMLAttributes<HTMLAnchorElement> {}

const ActionListItem = forwardRef<HTMLAnchorElement, ActionListItemProps>(
  ({ onFocus, className, text, link, target, variant = 'default', onActionClick, ...props }, ref) => {
    return (
      <Link
        {...props}
        ref={ref}
        href={link}
        target={target ?? '_self'}
        rel={target === '_blank' ? 'noopener noreferrer' : props.rel}
        className={cn(actionListVariants({ variant }), focusRingClasses, className)}
        data-testid={`${ActionListItemTestId}-${text}`}
        onFocus={onFocus}
        onClick={onActionClick}
      >
        <span className="line-clamp-2 break-words text-left font-graphik text-md font-medium leading-graphik">
          {text}
        </span>
        <div className="flex flex-row items-center justify-between">
          {props.sidetext && (
            <span className="line-clamp-2 break-words font-graphik text-md font-medium leading-graphik">
              {props.sidetext}
            </span>
          )}
          <ChevronRightIcon className="ml-1 size-6 shrink-0 stroke-2 transition-transform duration-300 ease-in-out group-hover:translate-x-2 sm:ml-2" />
        </div>
      </Link>
    );
  },
);
ActionListItem.displayName = 'ActionListItem';

export { ActionListItem, ActionListItemTestId };
